import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import { useApi, toastApiRef, ToastApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  User,
  UserEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ProvidersApi,
  providersApiRef,
} from '@internal/backstage-plugin-catalog-devops-react';
import { ActionTypes } from './types';
import { AdminApi, adminApiRef } from '../apis';

interface UsersContextProviderProps {
  children: ReactNode;
}

type LoadCompletedUsersAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: UserEntity[];
};

type DeleteUserAction = {
  type: ActionTypes.DELETE;
};

type DeleteCompletedUserAction = {
  type: ActionTypes.DELETE_COMPLETED;
};

type CreateCompletedUserAction = {
  type: ActionTypes.CREATE_COMPLETED;
};

type EditCompletedUserAction = {
  type: ActionTypes.EDIT_COMPLETED;
};
type CreateUserAction = {
  type: ActionTypes.CREATE;
  payload: User;
};

type EditUserAction = {
  type: ActionTypes.EDIT;
  payload: User;
};

type SelectUserAction = {
  type: ActionTypes.SELECT;
  payload: UserEntity;
};

type UnselectUserAction = {
  type: ActionTypes.UNSELECT;
};

type UsersAction =
  | LoadCompletedUsersAction
  | DeleteUserAction
  | CreateUserAction
  | EditUserAction
  | SelectUserAction
  | UnselectUserAction
  | DeleteCompletedUserAction
  | CreateCompletedUserAction
  | EditCompletedUserAction;

type UsersState = {
  users: UserEntity[];
  selectedUser: UserEntity | undefined;
  status: string;
  newUser: User | undefined;
};

type UsersContextType = {
  users: UserEntity[];
  selectedUser: UserEntity | undefined;
  isLoading: boolean;
  deleteUser(): void;
  createUser(user: User): void;
  editUser(user: User): void;
  selectUser(user: UserEntity): void;
  unselectUser(): void;
};

const initialUsersState = {
  users: [],
  selectedUser: undefined,
  status: 'initial_load',
  newUser: undefined,
};

export const UsersContext = createContext<UsersContextType | null>(null);

export function UsersContextProvider(
  props: Readonly<UsersContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const adminApi = useApi<AdminApi>(adminApiRef);
  const providersApi = useApi<ProvidersApi>(providersApiRef);

  const toastApi = useApi<ToastApi>(toastApiRef);

  const usersReducer = (state: UsersState, action: UsersAction): UsersState => {
    switch (action.type) {
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.users = action.payload;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.DELETE: {
        const stateCopy = { ...state };
        stateCopy.status = 'delete';
        return stateCopy;
      }
      case ActionTypes.DELETE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.selectedUser = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.CREATE: {
        const stateCopy = { ...state };
        stateCopy.status = 'create';
        stateCopy.newUser = action.payload;
        return stateCopy;
      }
      case ActionTypes.CREATE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.status = 'load';
        stateCopy.newUser = undefined;
        return stateCopy;
      }
      case ActionTypes.EDIT: {
        const stateCopy = { ...state };
        stateCopy.newUser = action.payload;
        stateCopy.status = 'edit';
        return stateCopy;
      }
      case ActionTypes.EDIT_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.newUser = undefined;
        stateCopy.selectedUser = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.SELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedUser = action.payload;
        return stateCopy;
      }
      case ActionTypes.UNSELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedUser = undefined;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(usersReducer, initialUsersState);

  useEffect(() => {
    const userFilter = { kind: 'user' };
    if (state.status === 'initial_load') {
      catalogApi.getEntities({ filter: userFilter }).then(value => {
        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: value.items as UserEntity[],
        });
      });
    }

    if (state.status === 'load') {
      new Promise(resolve => setTimeout(resolve, 2000)).then(() => {
        catalogApi.getEntities({ filter: userFilter }).then(value => {
          dispatch({
            type: ActionTypes.LOAD_COMPLETED,
            payload: value.items as UserEntity[],
          });
        });
      });
    }
  }, [catalogApi, state.status]);

  useEffect(() => {
    const createEntity = async () => {
      if (state.status !== 'create') return;
      if (state.newUser === undefined) return;

      const response = await adminApi.createUserEntity(state.newUser);

      if (response.succeeded) {
        await providersApi.triggerProvider('User');
        toastApi.post({
          title: `User "${state.newUser.UserId}" was created successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while creating user.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.CREATE_COMPLETED });
    };

    createEntity();
  }, [adminApi, providersApi, toastApi, state.status, state.newUser]);

  useEffect(() => {
    const editEntity = async () => {
      if (state.status !== 'edit') return;
      if (state.newUser === undefined) return;
      if (state.selectedUser === undefined) return;

      const response = await adminApi.editUserEntity(
        state.selectedUser.spec?.dynamic_uuid as string,
        state.newUser,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('User');
        toastApi.post({
          title: `User "${state.newUser.UserId}" was updated successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while updating user.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.EDIT_COMPLETED });
    };

    editEntity();
  }, [
    adminApi,
    providersApi,
    toastApi,
    state.status,
    state.selectedUser,
    state.newUser,
  ]);

  useEffect(() => {
    const deleteEntity = async () => {
      if (state.status !== 'delete') return;
      if (state.selectedUser === undefined) return;

      const response = await adminApi.deleteUserEntity(
        state.selectedUser.spec?.dynamic_uuid as string,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('User');
        toastApi.post({
          title: `User "${state.selectedUser.metadata.name}" was deleted successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while deleting user.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.DELETE_COMPLETED });
    };

    deleteEntity();
  }, [adminApi, providersApi, toastApi, state.status, state.selectedUser]);

  const deleteUser = () => dispatch({ type: ActionTypes.DELETE });
  const createUser = (user: User) =>
    dispatch({ type: ActionTypes.CREATE, payload: user });
  const editUser = (user: User) =>
    dispatch({ type: ActionTypes.EDIT, payload: user });
  const selectUser = (user: UserEntity) =>
    dispatch({ type: ActionTypes.SELECT, payload: user });
  const unselectUser = () => dispatch({ type: ActionTypes.UNSELECT });

  const context = useMemo(
    () => ({
      users: state.users,
      selectedUser: state.selectedUser,
      isLoading: state.status !== 'wait',
      deleteUser,
      createUser,
      editUser,
      selectUser,
      unselectUser,
    }),
    [state.users, state.selectedUser, state.status],
  );

  return (
    <UsersContext.Provider value={context}>
      {props.children}
    </UsersContext.Provider>
  );
}
